import { useRouter } from "next/router";

import { jobAddFormSchema } from "@/views/jobs/ForRecuriter/jobSchema";
import {
  RhCard,
  RhDatePickerFormik,
  RhFormGroup,
  RhFormGroupItem,
  RhIcon,
  RhInputFormik,
  RhLabel,
  RhSelectFormik,
  RhToast,
} from "@rhythm-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Form, Formik, FormikErrors } from "formik";
import React, { useEffect, useRef } from "react";

import { JobFormType } from "@/types/job";

import api from "@/services/api";
import {
  transformFormDataToJobData,
  transformJobDataToFormData,
} from "@/services/api/dataTransformers/jobModel";

import { joiningAvailabilityOptions } from "@/constants";

import formatCurrencyRange from "@/helpers/formatCurrencyRange";
import formatExperienceRange from "@/helpers/formatExperienceRange";

import { propToFormatCityLabelWithRhSelect } from "@/components/component-helper/propToFormatCityLabelWithRhSelect";
import {
  citySearchApiCall,
  jobRoleSearchApiCall,
} from "@/components/dataInputApiCalls";
import CheckBoxInputFormik from "@/components/formikInput/CheckBoxInputFormik";
import CompanySelectFormik from "@/components/formikInput/CompanySelectFormik";
import DataSelectFormik from "@/components/formikInput/DataSelectFormik";
import RadioInputFormik from "@/components/formikInput/RadioInputFormik";
import JobSkillPrioritization from "@/components/JobSkillPrioritization";
import { Button } from "@/components/ui/Button";
import RichTextEditor from "@/components/ui/RichTextEditor";

import JDFileUploader from "./JDFileUploader";

export const timeOptions = [
  { value: "00:00:00", label: "12:00 am" },
  { value: "00:30:00", label: "12:30 am" },
  { value: "01:00:00", label: "1:00 am" },
  { value: "01:30:00", label: "1:30 am" },
  { value: "02:00:00", label: "2:00 am" },
  { value: "02:30:00", label: "2:30 am" },
  { value: "03:00:00", label: "3:00 am" },
  { value: "03:30:00", label: "3:30 am" },
  { value: "04:00:00", label: "4:00 am" },
  { value: "04:30:00", label: "4:30 am" },
  { value: "05:00:00", label: "5:00 am" },
  { value: "05:30:00", label: "5:30 am" },
  { value: "06:00:00", label: "6:00 am" },
  { value: "06:30:00", label: "6:30 am" },
  { value: "07:00:00", label: "7:00 am" },
  { value: "07:30:00", label: "7:30 am" },
  { value: "08:00:00", label: "8:00 am" },
  { value: "08:30:00", label: "8:30 am" },
  { value: "09:00:00", label: "9:00 am" },
  { value: "09:30:00", label: "9:30 am" },
  { value: "10:00:00", label: "10:00 am" },
  { value: "10:30:00", label: "10:30 am" },
  { value: "11:00:00", label: "11:00 am" },
  { value: "11:30:00", label: "11:30 am" },
  { value: "12:00:00", label: "12:00 pm" },
  { value: "12:30:00", label: "12:30 pm" },
  { value: "13:00:00", label: "1:00 pm" },
  { value: "13:30:00", label: "1:30 pm" },
  { value: "14:00:00", label: "2:00 pm" },
  { value: "14:30:00", label: "2:30 pm" },
  { value: "15:00:00", label: "3:00 pm" },
  { value: "15:30:00", label: "3:30 pm" },
  { value: "16:00:00", label: "4:00 pm" },
  { value: "16:30:00", label: "4:30 pm" },
  { value: "17:00:00", label: "5:00 pm" },
  { value: "17:30:00", label: "5:30 pm" },
  { value: "18:00:00", label: "6:00 pm" },
  { value: "18:30:00", label: "6:30 pm" },
  { value: "19:00:00", label: "7:00 pm" },
  { value: "19:30:00", label: "7:30 pm" },
  { value: "20:00:00", label: "8:00 pm" },
  { value: "20:30:00", label: "8:30 pm" },
  { value: "21:00:00", label: "9:00 pm" },
  { value: "21:30:00", label: "9:30 pm" },
  { value: "22:00:00", label: "10:00 pm" },
  { value: "22:30:00", label: "10:30 pm" },
  { value: "23:00:00", label: "11:00 pm" },
  { value: "23:30:00", label: "11:30 pm" },
];

const emptyInitialForm: JobFormType = {
  title: undefined,
  job_role: undefined,
  job_type: undefined,
  company: undefined,
  handpick_company: undefined,
  min_exp: "",
  max_exp: "",
  negotiable_min_exp_bool: false,
  negotiable_min_exp: "",
  joining_availability_days__form_value: undefined,
  negotiable_min_joining_availability_bool: false,
  negotiable_min_joining_availability: undefined,
  min_salary: undefined,
  max_salary: undefined,
  negotiable_max_salary_bool: false,
  negotiable_max_salary: "",
  openings: undefined,
  job_description: undefined,
  screening_questions: undefined,
  location_new: [],
  work_preference_new__form_value: {
    Hybrid: false,
    Onsite: false,
    Remote: false,
  },
  is_public: "false",
  owner: [],
  contacts: [],
  status: "Active",
  is_codemonk_handpick: "false",
  interview_rounds: [],
  target_date: [],
  priority_skills__form_value: [],
  team_size: undefined,
  note: `1. TAT from employer to review the profiles?


2. No. of interviews can the client take in a week?


3. Can the client process candidates with Date of Joining within 10 days?




Any Special Requirement

`,
  is_experience_range_any: false,
};

const JobDetailsForm = ({
  setCreatedJobId,
  setStepperIndex,
  createdJobId,
}: {
  setCreatedJobId: React.Dispatch<React.SetStateAction<number | null>>;
  setStepperIndex: React.Dispatch<React.SetStateAction<number>>;
  createdJobId: number | null;
}) => {
  // const queryClient = useQueryClient();
  const postJob = useMutation({
    mutationFn: (data: JobFormType) => {
      if (!data.title) {
        RhToast.error("Job Title is required", {
          position: "bottom-right",
        });
        return Promise.reject();
      }
      if (!data.company) {
        RhToast.error("Company is required", {
          position: "bottom-right",
        });
        return Promise.reject();
      }

      if (data.is_codemonk_handpick === "true" && !data.handpick_company?.id) {
        RhToast.error("Codemonk Client is required", {
          position: "bottom-right",
        });
        return Promise.reject();
      }

      const payload = transformFormDataToJobData(data, [
        "title",
        "job_role",
        "job_type",
        "company.id",
        "handpick_company.id",
        "min_exp",
        "max_exp",
        "negotiable_min_exp",
        "min_salary",
        "max_salary",
        "openings",
        "job_description",
        "screening_questions",
        "location_new",
        "work_preference_new",
        "contacts",
        "status",
        "is_codemonk_handpick",
        "is_public",
        "sensitive_fields",
        "target_date",
        "team_size",
        "joining_availability_days",
        "negotiable_min_joining_availability",
        "negotiable_max_salary",
        "working_days",
        "working_hours_start",
        "working_hours_end",
        "note",
        "priority_skills",
        "primary_skills",
        "other_skills",
        "is_experience_range_any",
        "is_min_exp_negotiable",
        "is_joining_availability_negotiable",
        "is_max_salary_negotiate",
        "job_address",
      ]);
      return api.company.postJob({
        ...payload,
        is_draft: true,
      });
    },
    onSuccess: async (res, formData) => {
      try {
        if (pdfId.current) {
          await api.company.updateJobPdfStatus(pdfId.current, {
            job_requirement: res.data.id,
          });
        }

        if (formData.has_pressed_save_as_draft_btn) {
          router.push("/jobs");
          RhToast.success("Job saved as draft", {
            position: "bottom-right",
          });
        } else {
          setStepperIndex(1);
          setCreatedJobId(res.data.id);
          RhToast.success("Job added successfully", {
            position: "bottom-right",
          });
        }
      } catch (error) {
        RhToast.error("Something went wrong!", {
          position: "bottom-right",
        });
      }
    },
  });
  const queryClient = useQueryClient();
  //
  const patchJob = useMutation({
    mutationFn: (data: JobFormType) => {
      if (typeof createdJobId !== "number")
        return Promise.reject(new Error("id not valid"));

      const payload = transformFormDataToJobData(data, [
        "title",
        "job_role",
        "job_type",
        "company.id",
        "handpick_company.id",
        "min_exp",
        "max_exp",
        "negotiable_min_exp",
        "min_salary",
        "max_salary",
        "openings",
        "job_description",
        "screening_questions",
        "location_new",
        "work_preference_new",
        "contacts",
        "status",
        "is_codemonk_handpick",
        "is_public",
        "sensitive_fields",
        "target_date",
        "team_size",
        "joining_availability_days",
        "negotiable_min_joining_availability",
        "negotiable_max_salary",
        "working_days",
        "working_hours_start",
        "working_hours_end",
        "note",
        "priority_skills",
        "primary_skills",
        "other_skills",
        "is_experience_range_any",
        "is_min_exp_negotiable",
        "is_joining_availability_negotiable",
        "is_max_salary_negotiate",
        "job_address",
      ]);
      return api.company.patchJob({
        ...payload,
        id: createdJobId,
        is_draft: true,
      });
    },
    onSuccess: async (res, formData) => {
      if (pdfId.current) {
        await api.company.updateJobPdfStatus(pdfId.current, {
          job_requirement: createdJobId,
        });
      }

      if (formData.has_pressed_save_as_draft_btn) {
        router.push("/jobs");
        RhToast.success("Job saved as draft", {
          position: "bottom-right",
        });
      } else {
        setStepperIndex((state) => state + 1);
        RhToast.success("Job updated successfully", {
          position: "bottom-right",
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: createdJobId }],
      });
    },
  });
  const {
    data: jobDetail,
    isLoading: isJobDetailLoading,
    fetchStatus: jobDetailFetchStatus,
  } = useQuery({
    queryFn: () =>
      typeof createdJobId !== "number"
        ? Promise.reject(new Error("invalid job id"))
        : api.company.getJobById(createdJobId),
    queryKey: ["JOBS", "ITEM", { jobId: createdJobId }],
    enabled: typeof createdJobId === "number",
    select: (res) => res.data,
  });

  const pdfId = useRef<null | number>(null);

  const router = useRouter();

  const submitForm = (formData: JobFormType) => {
    if (!createdJobId) {
      postJob.mutate(formData);
    } else {
      patchJob.mutate(formData);
    }
  };

  if (isJobDetailLoading && jobDetailFetchStatus === "fetching")
    return (
      <div className="mx-auto mt-tm2">
        <p>Loading...</p>
      </div>
    );

  return (
    <Formik
      initialValues={
        jobDetail ? transformJobDataToFormData(jobDetail) : emptyInitialForm
      }
      enableReinitialize
      validationSchema={jobAddFormSchema}
      onSubmit={submitForm}
    >
      {({ values, setFieldValue, errors, touched, submitCount }) => (
        <Form className="background-blue flex flex-1 flex-col overflow-auto">
          <div className="background-blue flex-1 overflow-auto px-tnano py-tm2 2xl:container lg:px-tlg 2xl:mx-auto">
            <div className="flex gap-tm2 overflow-auto">
              <RhCard className="basis-[800px] space-y-tm3 p-tm2">
                <JDFileUploader
                  pdfId={pdfId}
                  createdJobId={createdJobId}
                  alreadyUploadedResume={jobDetail?.file_url}
                />

                <div className="space-y-tpico">
                  <h4>Basic Details</h4>

                  <p className="text-hint">
                    Fill basic details which will help candidates to understand
                    about job
                  </p>
                </div>

                <div>
                  <RhInputFormik
                    block
                    type="text"
                    name="title"
                    label="Job Title"
                    className="max-w-[320px]"
                  />
                </div>

                <div className="max-w-[320px]">
                  <RhLabel required>Job Role</RhLabel>

                  <DataSelectFormik
                    valueKey={"job_role"}
                    apiCall={jobRoleSearchApiCall}
                    setFormikFieldValue={setFieldValue}
                    values={values}
                  />
                </div>

                <div>
                  <RhLabel required>Job Type</RhLabel>
                  <RadioInputFormik
                    alignment="Horizontal"
                    options={[
                      { value: "Full-Time", label: "Full time" },
                      { value: "Contract", label: "Contract" },
                      {
                        value: "Internship",
                        label: "Internship",
                      },
                      {
                        value: "Freelancer",
                        label: "Freelancer",
                      },
                    ]}
                    name={`job_type`}
                  />
                </div>

                <div className="">
                  <RhLabel required>Experience Range (y)</RhLabel>
                  <div className="flex max-w-[320px]">
                    <div className="flex-1">
                      <RhLabel>Min</RhLabel>
                      <RhInputFormik
                        name="min_exp"
                        className="w-full"
                        type={"number"}
                        onWheel={(e: any) => e.target.blur()}
                        min={0}
                      />
                    </div>
                    <span className="m-tnano mt-tm3">-</span>
                    <div className="flex-1">
                      <RhLabel>Max</RhLabel>
                      <RhInputFormik
                        name="max_exp"
                        className="w-full"
                        type={"number"}
                        disabled={!!values.is_experience_range_any}
                        onWheel={(e: any) => e.target.blur()}
                      />

                      <div className="flex items-center gap-tnano">
                        <RhInputFormik
                          name="is_experience_range_any"
                          type={"checkbox"}
                          onClick={() => setFieldValue("max_exp", "")}
                        />
                        <p>Any</p>
                      </div>
                    </div>
                  </div>
                  <div className="">
                    <div className="flex items-center gap-tnano">
                      <RhInputFormik
                        name="is_min_exp_negotiable"
                        type={"checkbox"}
                        onClick={() => setFieldValue("negotiable_min_exp", "")}
                      />
                      <p>Negotiable minimum experience</p>
                      <div className="pl-[150px]">
                        {(values.min_exp || values.max_exp) && (
                          <p className="text-primary-600">{`"${formatExperienceRange.number({ min: values.min_exp, max: values.max_exp })} of exp ${values.negotiable_min_exp ? `, negotiable up to ${values.negotiable_min_exp}y` : ""}"`}</p>
                        )}
                      </div>
                    </div>

                    {values.is_min_exp_negotiable && (
                      <div className="mt-tnano">
                        <RhLabel required>Negotiable Min Experience</RhLabel>
                        <RhInputFormik
                          name="negotiable_min_exp"
                          type="number"
                          onWheel={(e: any) => e.target.blur()}
                          className="max-w-[120px]"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="">
                  <RhLabel required>Joining Availability</RhLabel>
                  <RhSelectFormik
                    name="joining_availability_days__form_value"
                    options={joiningAvailabilityOptions}
                    menuPortalTarget={
                      typeof window !== "undefined" &&
                      document.querySelector("#theme-provider")
                    }
                    className="max-w-[320px]"
                  />
                  <div className="flex items-center gap-tnano">
                    <RhInputFormik
                      name="is_joining_availability_negotiable"
                      type={"checkbox"}
                      onClick={() =>
                        setFieldValue(
                          "negotiable_min_joining_availability__form_value",
                          undefined,
                        )
                      }
                    />
                    <p className="whitespace-nowrap">
                      Negotiable minimum joining availability
                    </p>

                    <div className="pl-[150px]">
                      {values.joining_availability_days__form_value && (
                        <p className="text-primary-600">{`"Required ${values.joining_availability_days__form_value.label} joiner${values.negotiable_min_joining_availability__form_value ? (values.negotiable_min_joining_availability__form_value.value === "999" ? ", negotiable up to any days" : `, negotiable up to ${values.negotiable_min_joining_availability__form_value.value} days`) : ""}"`}</p>
                      )}
                    </div>
                  </div>
                  {values.is_joining_availability_negotiable && (
                    <>
                      <RhLabel required className="!mt-tnano">
                        Negotiable Joining Availability
                      </RhLabel>
                      <RhSelectFormik
                        name="negotiable_min_joining_availability__form_value"
                        options={joiningAvailabilityOptions.filter(
                          (item) => item.value !== "0",
                        )}
                        menuPortalTarget={
                          typeof window !== "undefined" &&
                          document.querySelector("#theme-provider")
                        }
                        className="max-w-[320px]"
                      />
                    </>
                  )}
                </div>

                <div>
                  <RhLabel required>Work Type</RhLabel>
                  <RhFormGroup>
                    <div className="mb-tsm flex gap-tm2">
                      {values.work_preference_new__form_value &&
                        Object.keys(values.work_preference_new__form_value).map(
                          (item, idx) => (
                            <RhFormGroupItem
                              key={idx}
                              control={
                                <RhInputFormik
                                  type="checkbox"
                                  name={`work_preference_new__form_value[${item}]`}
                                  error={
                                    (touched.work_preference_new__form_value ||
                                      submitCount > 1) &&
                                    !!errors.work_preference_new__form_value
                                  }
                                />
                              }
                              label={item}
                            />
                          ),
                        )}
                      <span className="text-xs text-red-500">
                        {(touched.work_preference_new__form_value ||
                          submitCount > 1) &&
                          !!errors.work_preference_new__form_value &&
                          "Please select job location type"}
                      </span>
                    </div>
                  </RhFormGroup>
                </div>

                {values.work_preference_new__form_value &&
                  (values.work_preference_new__form_value.Hybrid ||
                    values.work_preference_new__form_value.Onsite) && (
                    <div className="max-w-[320px]">
                      <RhLabel required>Work Location</RhLabel>
                      <DataSelectFormik
                        className="z-10"
                        values={values}
                        valueKey={`location_new`}
                        setFormikFieldValue={setFieldValue}
                        apiCall={citySearchApiCall}
                        isMulti
                        placeholder="Search"
                        {...propToFormatCityLabelWithRhSelect}
                      />
                    </div>
                  )}

                <div className="">
                  <RhLabel required>Salary Range (lpa)</RhLabel>
                  <div className="flex max-w-[320px]">
                    <div className="flex-1">
                      <RhLabel>Min</RhLabel>
                      <RhInputFormik
                        name="min_salary"
                        className="w-full"
                        type={"number"}
                        onWheel={(e: any) => e.target.blur()}
                      />
                    </div>
                    <span className="m-tnano mt-tm3">-</span>
                    <div className="flex-1">
                      <RhLabel>Max</RhLabel>
                      <RhInputFormik
                        name="max_salary"
                        className="w-full"
                        type={"number"}
                        onWheel={(e: any) => e.target.blur()}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-tnano">
                    <RhInputFormik
                      name="is_max_salary_negotiate"
                      type={"checkbox"}
                      onClick={() => setFieldValue("negotiable_max_salary", "")}
                    />
                    <p>Negotiable Max Salary</p>

                    <div className="pl-[150px]">
                      {(values.min_salary || values.max_salary) && (
                        <p className="text-primary-600">{`"${formatCurrencyRange({ min: values.min_salary ? values.min_salary * 100000 : null, max: values.max_salary ? values.max_salary * 100000 : null })} ${values.negotiable_max_salary ? `, negotiable up to ${values.negotiable_max_salary}lpa` : ""}"`}</p>
                      )}
                    </div>
                  </div>
                  {values.is_max_salary_negotiate && (
                    <div className="mt-tnano">
                      <RhLabel required>Negotiable Max Salary (lpa)</RhLabel>
                      <RhInputFormik
                        name="negotiable_max_salary"
                        type="number"
                        onWheel={(e: any) => e.target.blur()}
                        className="max-w-[120px]"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <RhLabel required>Number of Openings</RhLabel>
                  <RhInputFormik
                    name="openings"
                    type="number"
                    onWheel={(e: any) => e.target.blur()}
                  />
                </div>

                <div className="my-tm3 border-b" />

                <div className="">
                  <h4>Skills</h4>
                  <p className="text-hint">
                    Add skills and the min required exp here
                  </p>
                </div>

                <JobSkillPrioritization
                  values={values}
                  setFieldValue={setFieldValue}
                />

                <div className="my-tm3 border-b" />

                <div className="space-y-tpico">
                  <h4>Job Description</h4>
                  <p className="text-hint">
                    Add roles & responsibilities of the candidate and other
                    requirements, if any
                  </p>
                </div>

                <RichTextEditor
                  value={values.job_description}
                  onChange={(value) => {
                    setFieldValue("job_description", value);
                  }}
                />

                <div className="my-tm3 border-b" />

                <div className="space-y-tpico">
                  <h4>
                    Screening Questions{" "}
                    <span className="p-class text-hint">
                      (These questions will be shown in call log)
                    </span>
                  </h4>
                  <p className="text-hint">
                    Include any additional questions that should be asked during
                    candidate screening
                  </p>
                </div>

                <RichTextEditor
                  value={values.screening_questions}
                  onChange={(value) => {
                    setFieldValue("screening_questions", value);
                  }}
                />

                <div className="my-tm3 border-b" />

                <div className="space-y-tpico">
                  <h4>Employer Details</h4>
                  <p className="text-hint">Add hiring employer details</p>
                </div>

                <div>
                  <RhLabel>Is the hiring for?</RhLabel>
                  <RadioInputFormik
                    name="is_codemonk_handpick"
                    options={[
                      {
                        label: "Zelevate",
                        value: "false",
                      },
                      {
                        label: "Codemonk",
                        value: "true",
                      },
                    ]}
                    alignment="Horizontal"
                  />
                </div>

                <FormPublicSetter
                  values={values}
                  setFieldValue={setFieldValue}
                />

                <>
                  <div className="max-w-[320px]">
                    <RhLabel required>Employer Name</RhLabel>
                    <CompanySelectFormik showOnlyClients name={"company"} />
                    {values.is_codemonk_handpick === "true" && (
                      <div className="flex items-center gap-tmini">
                        <RhIcon icon="tabler:info-circle"></RhIcon>
                        <p>
                          You have selected <strong>Codemonk Handpicked</strong>
                          {`. Please
    select 'Codemonk' in Company field above`}
                        </p>
                      </div>
                    )}
                  </div>

                  {values.is_codemonk_handpick === "true" && (
                    <div>
                      <RhLabel required>Codemonk Client</RhLabel>
                      <CompanySelectFormik
                        showOnlyClients
                        name={"handpick_company"}
                        className="w-[320px]"
                      />
                    </div>
                  )}
                </>

                <div>
                  <RhLabel required>Should the job be?</RhLabel>
                  <RadioInputFormik
                    name="is_public"
                    options={[
                      {
                        label: "Public (visible to all)",
                        value: "true",
                      },
                      {
                        label: "Private (visible to recruiters only)",
                        value: "false",
                      },
                    ]}
                    alignment="Horizontal"
                  />
                </div>

                {values.is_public === "true" && (
                  <div>
                    <RhLabel>Sensitive Fields</RhLabel>
                    <CheckBoxInputFormik
                      name="sensitive_fields"
                      options={[
                        {
                          label: "Min Salary",
                          value: "min_salary",
                        },
                        {
                          label: "Max Salary",
                          value: "max_salary",
                        },
                      ]}
                      alignment="Horizontal"
                    />
                  </div>
                )}

                <div className="my-tm3 border-b" />

                <div className="space-y-tpico">
                  <h4>Team Size</h4>
                  <p className="text-hint">
                    Add no. of members will be in the team
                  </p>
                </div>

                <div>
                  <RhInputFormik
                    type="number"
                    onWheel={(e: any) => e.target.blur()}
                    name="team_size"
                    label="Team Members"
                    className="max-w-[320px]"
                    isOptional
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;
                      // If the input is cleared, set the value to null
                      setFieldValue(
                        "team_size",
                        value === "" ? undefined : Number(value),
                      );
                    }}
                  />
                </div>

                <div className="my-tm3 border-b" />

                <div className="space-y-tpico">
                  <h4>Working Hours & Days</h4>
                  <p className="text-hint">
                    Add working hours in a day and no. of working days in a week
                  </p>
                </div>

                <div>
                  <RhLabel>Working Hours</RhLabel>

                  <div className="flex max-w-[320px] items-center gap-tnano">
                    <div className="flex-1">
                      <RhSelectFormik
                        name="working_hours_start__form_value"
                        options={timeOptions}
                        className="w-full"
                        placeholder=""
                      />
                    </div>

                    <span className="mt-[-20px]">-</span>

                    <div className="flex-1">
                      <RhSelectFormik
                        name="working_hours_end__form_value"
                        options={timeOptions}
                        placeholder=""
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="max-w-[320px]">
                  <RhInputFormik
                    name="working_days"
                    type="text"
                    label="Working Days"
                    block
                    isOptional
                  />
                </div>

                <div className="my-tm3 border-b" />

                <div className="space-y-tpico">
                  <h4>Other Details</h4>
                  <p className="text-hint">
                    This will be only visible to recruiters
                  </p>
                </div>

                <div>
                  <RhLabel>Target Date</RhLabel>
                  <div className="[&>div]:w-[320px]">
                    <RhDatePickerFormik
                      options={{
                        enableTime: false,
                        minDate: dayjs().format(),
                      }}
                      name="target_date"
                    ></RhDatePickerFormik>
                  </div>
                </div>

                <div>
                  <RhLabel>Job Address</RhLabel>
                  <div className="[&>div]:w-[320px]">
                    <DataSelectFormik
                      apiCall={citySearchApiCall}
                      setFormikFieldValue={setFieldValue}
                      values={values}
                      valueKey={"job_address"}
                    />
                  </div>
                </div>
              </RhCard>
              <div className="flex-1 space-y-tnano">
                <h4>
                  Notes{" "}
                  <span className="p-class text-hint">
                    (Only visible to recruiters)
                  </span>
                </h4>
                <div className="h-[70vh] [&>div]:h-full">
                  <RhInputFormik
                    name="note"
                    type="textarea"
                    className="h-full w-full"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex h-[56px] items-center justify-end gap-2 border border-t border-gray-200 bg-white px-tlg py-tnano">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => router.push("/jobs")}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={"secondary"}
              onClick={() => {
                postJob.mutate({
                  ...values,
                  has_pressed_save_as_draft_btn: true,
                });
              }}
            >
              Save As Draft
            </Button>
            <Button type="submit" data-testid="next-btn-jobs">
              Next
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default JobDetailsForm;

const FormPublicSetter = ({
  setFieldValue,
  values,
}: {
  values: JobFormType;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<JobFormType>>;
}) => {
  useEffect(() => {
    if (values.is_codemonk_handpick === "true") {
      setFieldValue("is_public", "false");
    }
  }, [values.is_codemonk_handpick]);

  return null;
};
